import React, { useState, useEffect, useCallback } from "react";
import styles from "./PolicyCorrectionModal.module.css";
import { toast } from "react-toastify";
import FixedAgent from "../../utils/api/FixedAgent";
import { useSelector } from "react-redux";
import { CEI_MAKES, NICON_MAKES } from "../../utils/constants/vehicleData";

const EMPTY_FORM = {
  insured_name: "",
  phone: "",
  chasis_no: "",
  engine_no: "",
  year: "",
  vehicle_make: "",
  vehicle_model: "",
  color: "",
  email: "",
  // effectiveDate: "",
  plate_no: "",
  address: "",
};

const PolicyCorrectionModal = ({ isOpen, onClose, orderData }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [policyInfo, setPolicyInfo] = useState(null);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const token = useSelector((state) => state.userData.loginData.token);

  // ─── Fetch policy info on open ────────────────────────────────────────────
  const fetchPolicyInfo = useCallback(
    async (plateNumber) => {
      if (!plateNumber) return;
      setLoadingPolicy(true);
      try {
        const res = await FixedAgent.getPolicyInformation({
          policyOrPlateNumber: plateNumber,
          token,
        });
        const data = res.data.data;
        setPolicyInfo(data);

        // Determine make list by platform
        const platform = (data.platform || "").toUpperCase();
        const makeList = platform === "NICON" ? NICON_MAKES : CEI_MAKES;
        setMakes(makeList);

        // Find the matching make (case-insensitive)
        const matchedMake = makeList.find(
          (m) =>
            m.name.toLowerCase() === (data.vehicle_brand || "").toLowerCase(),
        );
        const formattedPhone = "0" + data.phone_number.replace(/\D/g, "").slice(-10);
        setFormData({
          insured_name: data.insurer_name || "",
          phone: formattedPhone || "",
          chasis_no: data.chasis_no || "",
          engine_no: data.engine_no || "",
          year: data.year_of_make || "",
          vehicle_make: matchedMake?.id || "",
          vehicle_model: "", // populated after models load
          color: data.color || "",
          email: data.email || "",
          effectiveDate: data.effective_start_date || "",
          plate_no: data.registration_number || plateNumber,
          address: data.address || "",
        });

        // If we matched a make, fetch models immediately
        if (matchedMake?.id) {
          fetchModels(matchedMake.id, data.vehicle_model);
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Failed to fetch policy info",
        );
      } finally {
        setLoadingPolicy(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [token],
  );

  // ─── Fetch models for a given make_id ─────────────────────────────────────
  const fetchModels = useCallback(
    async (makeId, autoSelectModelName = null) => {
      if (!makeId) return;
      setLoadingModels(true);
      setModels([]);
      try {
        const res = await FixedAgent.getVehicleModelsByMakeId({
          make_id: makeId,
          token,
        });
        const responseData = res.data;
        let modelList = [];
        if (responseData?.data?.data?.model) {
          modelList = responseData.data.data.model;
        } else if (responseData?.data?.model) {
          modelList = responseData.data.model;
        } else if (Array.isArray(responseData?.data)) {
          modelList = responseData.data;
        }
        setModels(modelList || []);

        if (autoSelectModelName) {
          const matched = modelList.find(
            (m) =>
              (m.name || m.model_name || "").toLowerCase() ===
              autoSelectModelName.toLowerCase(),
          );
          if (matched) {
            setFormData((prev) => ({
              ...prev,
              vehicle_model: matched.id || matched.model_id || "",
            }));
          }
        }
      } catch (err) {
        toast.error("Failed to load vehicle models");
      } finally {
        setLoadingModels(false);
      }
    },
    [token],
  );

  // ─── Trigger fetch when modal opens ───────────────────────────────────────
  useEffect(() => {
    if (isOpen && orderData?.plate_no) {
      setPolicyInfo(null);
      setModels([]);
      setFormData(EMPTY_FORM);
      fetchPolicyInfo(orderData.plate_no);
    }
  }, [isOpen, orderData, fetchPolicyInfo]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleMakeChange = (e) => {
    const newMakeId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      vehicle_make: newMakeId,
      vehicle_model: "",
    }));
    setModels([]);
    if (newMakeId) fetchModels(newMakeId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await FixedAgent.policyCorrection({
        plateNumber: formData.plate_no,
        token,
        data: formData,
      });
      toast.success("Policy correction submitted successfully");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal_overlay} onClick={onClose}>
      <div className={styles.modal_card} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modal_header}>
          <h2>Policy Correction</h2>
          <button className={styles.close_button} onClick={onClose}>
            &times;
          </button>
        </div>

        {loadingPolicy ? (
          <div className={styles.loading_state}>
            <div className={styles.spinner} />
            <p>Fetching policy details…</p>
          </div>
        ) : (
          <>
            {policyInfo && (
              <div className={styles.policy_badge}>
                <span className={styles.badge_label}>Policy #</span>
                <span className={styles.badge_value}>
                  {policyInfo.policy_number}
                </span>
                <span
                  className={`${styles.badge_status} ${policyInfo.policy_status === "completed" ? styles.status_active : ""}`}
                >
                  {policyInfo.policy_status}
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className={styles.form_group}>
                <label>Full Name</label>
                <input
                  type="text"
                  name="insured_name"
                  value={formData.insured_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className={styles.form_group}>
                <label>Phone Number</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className={styles.form_group}>
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.form_group}>
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              {/* <div className={styles.form_group}>
                <label>Plate Number</label>
                <input
                  type="text"
                  name="plate_no"
                  value={formData.plate_no}
                  onChange={handleChange}
                  required
                />
              </div> */}
              <div className={styles.form_group}>
                <label>Chassis Number</label>
                <input
                  type="text"
                  name="chasis_no"
                  value={formData.chasis_no}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.form_group}>
                <label>Engine Number</label>
                <input
                  type="text"
                  name="engine_no"
                  value={formData.engine_no}
                  onChange={handleChange}
                />
              </div>

              {/* Make dropdown */}
              <div className={styles.form_group}>
                <label>Vehicle Make</label>
                <select
                  name="vehicle_make"
                  value={formData.vehicle_make}
                  onChange={handleMakeChange}
                  required
                >
                  <option value="">-- Select Make --</option>
                  {makes.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Model dropdown */}
              <div className={styles.form_group}>
                <label>Vehicle Model</label>
                {loadingModels ? (
                  <div className={styles.inline_loading}>Loading models…</div>
                ) : (
                  <select
                    name="vehicle_model"
                    value={formData.vehicle_model}
                    onChange={handleChange}
                    required
                    disabled={!formData.vehicle_make || models.length === 0}
                  >
                    <option value="">-- Select Model --</option>
                    {models.map((m) => {
                      const id = m.id || m.model_id;
                      const name = m.name || m.model_name;
                      return (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>

              <div className={styles.form_group}>
                <label>Year</label>
                <input
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.form_group}>
                <label>Color</label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                />
              </div>
              {/* <div className={styles.form_group}>
                <label>Effective Date</label>
                <input
                  type="date"
                  name="effectiveDate"
                  value={formData.effectiveDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div> */}

              <button
                type="submit"
                className={styles.submit_button}
                disabled={submitting || loadingPolicy}
              >
                {submitting ? "Submitting…" : "Submit Correction"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PolicyCorrectionModal;
